import { useEffect, useState } from 'react'
import { Icon, Mark } from '../ui'
import { useAppStore } from '../../store/appStore'
import { useCapabilities } from '../../hooks/useCapabilities'
import { canSwitchRoles } from '../../lib/capabilities'
import { NotificationsPanel } from './NotificationsPanel'
import { YearSwitcher } from './YearSwitcher'
import { AvatarMenu } from './AvatarMenu'
import { notifUnread, type NotifAction } from '../../lib/notifications'
import type { Profile } from '../../lib/types'

interface TopbarProps {
  onSignOut: () => void
  profile: Profile | null
}

const PHASE_DOTS: Record<string, string> = {
  fall: '#b3823a',
  midyear: '#2f6f82',
  spring: '#4a8a60',
}

export function Topbar({ onSignOut, profile }: TopbarProps) {
  const { role, phase, nav, reviewId, drawerOpen, setRole, setPhase, setNav, setReviewId, setDrawerOpen, openSettings } =
    useAppStore()
  const caps = useCapabilities()
  const [notifOpen, setNotifOpen] = useState(false)
  const [unread, setUnread] = useState(() => notifUnread(role))

  useEffect(() => {
    setUnread(notifUnread(role))
  }, [role, notifOpen])

  // Show phase bar except on the admin dashboard root.
  const showPhaseBar = !(role === 'admin' && nav.page === 'dashboard' && !reviewId)

  function handleNotifAct(go: NotifAction) {
    setReviewId(null)
    if (go.type === 'review') {
      setRole('admin')
      setNav({ page: 'dashboard' })
    } else if (go.type === 'page') {
      if (go.page === 'forms' || go.page === 'dashboard') {
        setRole('admin')
        setNav({ page: go.page })
      } else {
        setRole('teacher')
        setNav({ page: go.page })
      }
    } else {
      setRole('teacher')
      setNav({ page: 'domain', domain: go.domain, code: go.code })
    }
  }

  return (
    <header className="topbar">
      <button className="menu-btn" aria-label="Open navigation" onClick={() => setDrawerOpen(!drawerOpen)}>
        <Icon name="menu" size={22} />
      </button>

      <div className="brand">
        <div className="mark">
          <Mark />
        </div>
        <div>
          <div>GrowthLane</div>
          <div className="sub">Cedar Valley SD</div>
        </div>
      </div>

      <div className="spacer" />

      {showPhaseBar && (
        <div className="phasebar hide-sm">
          {(['fall', 'midyear', 'spring'] as const).map((p) => (
            <button key={p} className={phase === p ? 'on' : ''} onClick={() => setPhase(p)}>
              <span className="ph-dot" style={{ background: PHASE_DOTS[p] }} />
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div className="hide-sm">
        <YearSwitcher />
      </div>

      {/* Bell + notifications */}
      <div className="av-wrap" onClick={(e) => e.stopPropagation()}>
        <button
          className="tb-btn tb-icon"
          aria-label="Notifications"
          onClick={() => setNotifOpen((o) => !o)}
        >
          <Icon name="bell" size={18} />
          {unread > 0 && <span className="tb-badge">{unread}</span>}
        </button>
        {notifOpen && (
          <NotificationsPanel
            role={role}
            onClose={() => setNotifOpen(false)}
            onAct={handleNotifAct}
            onOpenSettings={() => openSettings('notifications')}
          />
        )}
      </div>

      {canSwitchRoles(caps) && (
        <div className="roleswitch hide-sm">
          <button
            className={role === 'teacher' ? 'on' : ''}
            onClick={() => {
              setRole('teacher')
              setReviewId(null)
              setNav({ page: 'overview' })
            }}
          >
            <Icon name="user" size={14} />
            <span>Staff</span>
          </button>
          <button
            className={role === 'admin' ? 'on' : ''}
            onClick={() => {
              setRole('admin')
              setReviewId(null)
              setNav({ page: 'dashboard' })
            }}
          >
            <Icon name="shield" size={14} />
            <span>Admin</span>
          </button>
        </div>
      )}

      <div className="hide-sm">
        <AvatarMenu profile={profile} onSignOut={onSignOut} />
      </div>
    </header>
  )
}
